import type { UpgradeModifiers } from './runState';
import type { DamageSource } from './runState';
import type { RelicRarity } from './relicCatalog';
import type { PartnerType } from './shopCatalog';
import type { SynergyTag } from './SynergySystem';

export type RunDamageSource = PartnerType | 'relic' | 'unknown';

export interface DamageSourceStats {
  source: RunDamageSource;
  label: string;
  damage: number;
  hits: number;
  kills: number;
}

export interface DamageTakenSourceStats {
  source: DamageSource;
  label: string;
  damage: number;
  hits: number;
}

export type RunPurchaseKind =
  | 'buy-partner'
  | 'buy-relic'
  | 'sell-partner'
  | 'merge-partner'
  | 'reroll'
  | 'upgrade'
  | 'special-drop'
  | 'economy-grant';

export interface RunPurchaseEvent {
  index: number;
  wave: number;
  kind: RunPurchaseKind;
  itemId: string;
  label: string;
  cost: number;
  refund: number;
  level?: number;
  materialsAfter: number;
  note?: string;
}

export interface BuildPartnerSnapshot {
  type: PartnerType;
  level: number;
}

export interface BuildRelicSnapshot {
  id: string;
  name: string;
  count: number;
  rarity: RelicRarity;
  tag: string;
  contribution?: {
    damageBonus: number;
    attackSpeedBonus: number;
    maxHpDelta: number;
    materialGainBonus: number;
  };
}

export interface BuildSynergySnapshot {
  tag: SynergyTag;
  count: number;
  tiers: number[];
  description: string;
}

export interface RunBuildSnapshot {
  index: number;
  wave: number;
  stage: 'wave-complete' | 'shop-complete' | 'final';
  hp: number;
  maxHp: number;
  materials: number;
  level: number;
  partners: BuildPartnerSnapshot[];
  relics: BuildRelicSnapshot[];
  synergies: BuildSynergySnapshot[];
  upgradeModifiers: UpgradeModifiers;
}

export interface RunStatsSnapshot {
  damageDealtBySource: DamageSourceStats[];
  damageTakenBySource: DamageTakenSourceStats[];
  purchasePath: RunPurchaseEvent[];
  buildSnapshots: RunBuildSnapshot[];
  topDamageSource?: DamageSourceStats;
  topDamageTakenSource?: DamageTakenSourceStats;
}

export class RunStatsTracker {
  private readonly damageDealtBySource = new Map<RunDamageSource, DamageSourceStats>();
  private readonly damageTakenBySource = new Map<DamageSource, DamageTakenSourceStats>();
  private readonly purchasePath: RunPurchaseEvent[] = [];
  private readonly buildSnapshots: RunBuildSnapshot[] = [];

  recordDamage(source: RunDamageSource, amount: number) {
    if (!Number.isFinite(amount) || amount < 0) {
      throw new Error(`Invalid run damage amount: ${amount}`);
    }
    const entry = this.getDamageEntry(source);
    entry.damage += amount;
    entry.hits += 1;
  }

  recordKill(source: RunDamageSource) {
    const entry = this.getDamageEntry(source);
    entry.kills += 1;
  }

  recordDamageTaken(source: DamageSource, amount: number) {
    if (!Number.isFinite(amount) || amount < 0) {
      throw new Error(`Invalid taken damage amount: ${amount}`);
    }
    const entry = this.getDamageTakenEntry(source);
    entry.damage += amount;
    entry.hits += 1;
  }

  recordPurchase(event: Omit<RunPurchaseEvent, 'index'>) {
    this.purchasePath.push({
      ...event,
      index: this.purchasePath.length + 1,
    });
  }

  recordBuildSnapshot(snapshot: Omit<RunBuildSnapshot, 'index'>) {
    this.buildSnapshots.push({
      ...snapshot,
      index: this.buildSnapshots.length + 1,
    });
  }

  reset() {
    this.damageDealtBySource.clear();
    this.damageTakenBySource.clear();
    this.purchasePath.splice(0);
    this.buildSnapshots.splice(0);
  }

  snapshot(): RunStatsSnapshot {
    const damageDealtBySource = Array.from(this.damageDealtBySource.values())
      .map((entry) => ({ ...entry }))
      .sort((a, b) => b.damage - a.damage);
    const damageTakenBySource = Array.from(this.damageTakenBySource.values())
      .map((entry) => ({ ...entry }))
      .sort((a, b) => b.damage - a.damage);

    return {
      damageDealtBySource,
      damageTakenBySource,
      purchasePath: this.purchasePath.map((event) => ({ ...event })),
      buildSnapshots: this.buildSnapshots.map((snapshot) => ({
        ...snapshot,
        partners: snapshot.partners.map((partner) => ({ ...partner })),
        relics: snapshot.relics.map((relic) => ({ ...relic })),
        synergies: snapshot.synergies.map((synergy) => ({
          ...synergy,
          tiers: [...synergy.tiers],
        })),
        upgradeModifiers: { ...snapshot.upgradeModifiers },
      })),
      topDamageSource: damageDealtBySource[0] ? { ...damageDealtBySource[0] } : undefined,
      topDamageTakenSource: damageTakenBySource[0] ? { ...damageTakenBySource[0] } : undefined,
    };
  }

  private getDamageEntry(source: RunDamageSource) {
    const existing = this.damageDealtBySource.get(source);
    if (existing) {
      return existing;
    }
    const entry: DamageSourceStats = {
      source,
      label: getDamageSourceLabel(source),
      damage: 0,
      hits: 0,
      kills: 0,
    };
    this.damageDealtBySource.set(source, entry);
    return entry;
  }

  private getDamageTakenEntry(source: DamageSource) {
    const existing = this.damageTakenBySource.get(source);
    if (existing) {
      return existing;
    }
    const entry: DamageTakenSourceStats = {
      source,
      label: getTakenSourceLabel(source),
      damage: 0,
      hits: 0,
    };
    this.damageTakenBySource.set(source, entry);
    return entry;
  }
}

export function normalizeDamageSource(source?: PartnerType | 'relic'): RunDamageSource {
  return source ?? 'unknown';
}

function getDamageSourceLabel(source: RunDamageSource) {
  if (source === 'gunner') return '枪手';
  if (source === 'sword') return '剑士';
  if (source === 'mage') return '法师';
  if (source === 'nurse') return '护士';
  if (source === 'engineer') return '工匠';
  if (source === 'assassin') return '刺客';
  if (source === 'tank') return '重装';
  if (source === 'summoner') return '召唤师';
  if (source === 'relic') return '遗物';
  return '未知';
}

function getTakenSourceLabel(source: DamageSource) {
  if (source === 'shockwave') return 'Boss技能';
  if (source === 'boss-hazard') return 'Boss危险区';
  if (source === 'boss-projectile') return 'Boss弹幕';
  if (source === 'minion') return '小怪';
  return '碰撞';
}
